import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Bell, Check, SlidersHorizontal } from "lucide-react";
import {
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  NOTIFICATIONS_CHANGED,
  withoutTicketNumberPrefix,
  type AppNotification,
} from "@/lib/notifications";
import { ageOf } from "@/lib/tickets";
import { cn } from "@/lib/utils";

/**
 * The notifications bell.
 *
 * ⚠️ Every item here was composed for THIS person at the moment of the event, from that
 * person's own permissions (D-17), and the endpoint behind it is scoped twice before any of
 * this code runs — the query filter admits a row only when it was addressed to the caller
 * AND its ticket is still visible, and that second clause joins through Tickets so SQL
 * Row-Level Security refuses the join on its own. Somebody whose clearance is revoked after
 * a notification was composed cannot read it back off the bell.
 *
 * ⚠️ Subject lines only, never bodies. The stored bodies are HTML for email clients; in-app
 * rows are written without one, so there is nothing to sanitise and no `dangerouslySetInnerHTML`
 * anywhere near this component.
 *
 * ⚠️ It renders the same disabled shell it always did when the feature is off or the fetch
 * fails — nothing here is an authorization decision, and an empty bell is the honest answer
 * rather than an error toast on whatever page somebody happened to be using.
 */
export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AppNotification[] | null>(null);
  const [unread, setUnread] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const page = await getNotifications();
      setItems(page.items);
      setUnread(page.unreadCount);
    } catch {
      // Silent. The bell is chrome on every screen in the product, so a failure here must
      // not interrupt whatever the person was actually doing. It resolves to an empty bell,
      // which is what it looked like before this existed.
      setItems([]);
      setUnread(0);
    }
  }, []);

  // On mount and whenever the tab is brought back.
  //
  // ⚠️ NO POLLING, deliberately. The count is not urgent enough to justify a request per
  // person per interval across ~2,600 people, and the tab holding a stale number is by
  // definition the one nobody is looking at.
  //
  // ⚠️ `visibilitychange` as well as `focus`, because the two fire in different cases and
  // the one that matters here is the one `focus` misses. Switching browser tabs — the
  // commonest way somebody comes back to this app — raises `visibilitychange` and often
  // does not raise a window `focus` at all, so on its own the bell went stale for exactly
  // the person who had left it and returned. `focus` stays for the case
  // `visibilitychange` misses: another window on top of a still-visible tab.
  useEffect(() => {
    void load();

    const refresh = () => {
      if (document.visibilityState === "visible") void load();
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);

    // ⚠️ And when something else in the app clears a notification — opening a request marks
    // its updates read (see markTicketNotificationsRead). Without this the bell would keep
    // showing a count that the page underneath it has already cleared, until the tab lost and
    // regained focus. Still not polling: this fires only when a write actually marked rows.
    window.addEventListener(NOTIFICATIONS_CHANGED, load);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener(NOTIFICATIONS_CHANGED, load);
    };
  }, [load]);

  // Click-away and Escape. A dropdown that traps the pointer is worse than no dropdown.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function openBell() {
    const next = !open;
    setOpen(next);

    // Re-read on open rather than trusting what was fetched on mount. Somebody who has had
    // a tab open all afternoon should not have to reload to see this morning's updates.
    if (next) await load();
  }

  async function readOne(item: AppNotification) {
    setOpen(false);

    if (item.readAtUtc) return;

    // Optimistic: the row is already being navigated away from, and a failed mark-read
    // costs a badge that corrects itself on the next load. Nothing about access depends
    // on it.
    setUnread((n) => Math.max(0, n - 1));
    setItems((current) =>
      current === null
        ? null
        : current.map((i) =>
            i.id === item.id ? { ...i, readAtUtc: new Date().toISOString() } : i,
          ),
    );

    try {
      await markNotificationRead(item.id);
    } catch {
      void load();
    }
  }

  async function readAll() {
    setUnread(0);
    setItems((current) =>
      current === null
        ? null
        : current.map((i) => (i.readAtUtc ? i : { ...i, readAtUtc: new Date().toISOString() })),
    );

    try {
      await markAllNotificationsRead();
    } catch {
      void load();
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => void openBell()}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
        className="relative grid h-9 w-9 place-items-center rounded-lg text-muted-foreground transition hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Bell className="h-4 w-4" aria-hidden />

        {unread > 0 && (
          <span
            aria-hidden
            className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground"
          >
            {/* Capped, because a three-digit badge stops being a number and starts being a
                shape. The exact count past ninety-nine changes nothing anybody does. */}
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notifications"
          className="absolute right-0 z-50 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-border bg-popover shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <p className="text-sm font-medium">Notifications</p>

            {unread > 0 && (
              <button
                type="button"
                onClick={() => void readAll()}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground transition hover:bg-accent hover:text-foreground"
              >
                <Check className="h-3 w-3" aria-hidden />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[24rem] overflow-y-auto">
            {items === null ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-3 py-8 text-center text-sm text-muted-foreground">
                Nothing yet. Updates on your requests will show up here.
              </p>
            ) : (
              <ul>
                {items.map((item) => {
                  // ⚠️ An item that names no request is a follow-up reminder (D-95), and it opens
                  // the follow-ups screen rather than a ticket. Decided from `number` being
                  // absent, not from the event name: the server withholds the number precisely
                  // when there is nothing to route to, so this cannot disagree with it, whereas a
                  // string comparison against an event type is a second list to keep in step.
                  // ⚠️ The number comes off the sentence because the TITLE now carries it. The
                  // templates open "[#412]" for the mailbox, where a subject arrives with
                  // nothing around it; here it was printing twice, three lines apart. Stripped
                  // for this item's own number only — see the helper.
                  const event = withoutTicketNumberPrefix(item.subject, item.displayNumber);

                  // ⚠️ Whatever is NOT the number on the bottom line, joined rather than
                  // concatenated. Written out with separators it grew a leading or trailing
                  // "·" every time a part went missing — and parts do go missing: a follow-up
                  // reminder has no requester, and neither has an item whose request is out of
                  // reach. A join cannot produce a dangling separator.
                  const meta = [
                    item.displayNumber === null ? "Your follow-ups" : null,
                    item.requesterName,
                    `${ageOf(item.createdAtUtc)} ago`,
                  ].filter((part): part is string => part !== null);

                  const body = (
                    <>
                      <span
                        aria-hidden
                        className={cn(
                          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
                          item.readAtUtc ? "bg-transparent" : "bg-primary",
                        )}
                      />

                      <span className="min-w-0 flex-1">
                        {/* ⚠️ THE REQUEST'S OWN TITLE, falling back to the message's subject.
                            Every item used to read as its template's subject line, so a bell
                            with nine items said "We have your request" nine times over nine
                            different requests and the only thing telling them apart was a
                            number nobody has memorised. The title answers "which one is this"
                            in the place the eye lands first.

                            ⚠️ The fallback is not decoration. `ticketSubject` is null for a
                            follow-up reminder, which names no request on purpose (D-95), and
                            for an item whose request has gone out of reach — routed away,
                            reclassified, or one the reader was removed from. Both then read as
                            what happened rather than as a blank line. */}
                        <span className="block truncate font-medium">
                          {/* ⚠️ THE NUMBER LEADS THE TITLE, and it is the one part of the item
                              that must survive a narrow bell: `truncate` cuts from the END, so
                              on a phone "#249 · Locked out of the EM…" still says which request
                              this is, where a number at the other end would be the first thing
                              lost. It is also the reference people quote to each other and read
                              out on the phone (D-35).

                              ⚠️ Muted and lighter than the title beside it. Both are on the
                              line the eye lands on, and the title is what distinguishes one
                              item from the next — the number is the handle, not the headline. */}
                          {item.displayNumber !== null && (
                            <span className="font-normal text-muted-foreground">
                              #{item.displayNumber}
                              <span aria-hidden> · </span>
                            </span>
                          )}
                          {item.ticketSubject ?? event}
                        </span>

                        {/* ⚠️ WHAT HAPPENED, on its own line, once the title above stopped
                            carrying it — somebody replied, the status moved, you were added to
                            a request, this was withdrawn. Two things drop it.

                            ⚠️ No title to distinguish it from, or the same sentence prints
                            twice: that is a follow-up reminder, which names no request (D-95).

                            ⚠️ Or the sentence says nothing the item has not. Only the creation
                            receipt does — "We have your request" under the request's own title,
                            above its requester and age, where the notification EXISTING is the
                            receipt. A bell full of new requests was a column of one repeated
                            sentence.

                            ⚠️ THE SERVER DECIDES THAT, from the enum. Comparing `eventType`
                            here would be a second list of event names to keep in step with the
                            server's — the same reason the follow-up test above reads the absent
                            number rather than the event name. */}
                        {item.ticketSubject !== null && !item.subjectRestatesTheRequest && (
                          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                            {event}
                          </span>
                        )}

                        {/* Who it is about and how long ago. A display name and nothing else —
                            nothing here links to a person, so there is no id to carry (D-38). */}
                        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                          {meta.join(" · ")}
                        </span>
                      </span>
                    </>
                  );

                  const className = cn(
                    "flex gap-2.5 border-b border-border px-3 py-2.5 text-sm transition last:border-b-0 hover:bg-accent",
                    !item.readAtUtc && "bg-primary/5",
                  );

                  return (
                    <li key={item.id}>
                      {item.number === null ? (
                        <Link to="/todos" onClick={() => void readOne(item)} className={className}>
                          {body}
                        </Link>
                      ) : (
                        <Link
                          to="/tickets/$id"
                          // ⚠️ The OPAQUE number. The readable one is for reading (D-33, D-35).
                          params={{ id: item.number }}
                          onClick={() => void readOne(item)}
                          className={className}
                        >
                          {body}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* ⚠️ THE SWITCHES ALREADY EXISTED AND NOBODY COULD FIND THEM. D-101 shipped ten
              per-channel opt-outs on the profile — including "New requests on my teams", the one
              that empties a busy agent's mailbox — and they sat below the avatar, the contact
              panels and "Your access", on a screen people visit once. Somebody buried in intake
              mail does not go to their profile; they come here, to the thing making the noise.

              ⚠️ A LINK, not a control. Silencing something is a decision with copy attached —
              which channel, and the list of what can never be switched off — and none of that
              fits in a popover. Putting a switch here would also mean two places writing the same
              preference, which is how the bell and the profile come to disagree about what
              somebody asked for.

              ⚠️ It closes the panel on the way, or the popover stays open over the screen it
              just navigated to. */}
          <div className="border-t border-border px-3 py-2">
            <Link
              to="/profile"
              hash="notifications"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition hover:text-foreground"
            >
              <SlidersHorizontal className="h-3 w-3" aria-hidden />
              Choose what you are told about
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
